'use client';

/**
 * Composant principal de l'interface de recherche d'entités (auteurs, éditeurs, relieurs)
 *
 * Principe SOLID: Single Responsibility
 * - Responsable uniquement de l'orchestration des composants de recherche
 * - Délègue le rendu à des composants spécialisés
 *
 * Principe Open/Closed:
 * - Ouvert à l'extension via render props (renderHitCard)
 * - Fermé à la modification (pas besoin de modifier pour ajouter une entité)
 *
 * Principe Dependency Inversion:
 * - Dépend d'abstractions (render props, config déclarative)
 * - Pas de dépendance aux implémentations concrètes des cartes
 *
 * @module EntitySearchInterface
 */

import type { BaseHit } from 'instantsearch.js';
import React, { useCallback, useMemo, useState } from 'react';
import { Configure } from 'react-instantsearch';
import { InstantSearchNext } from 'react-instantsearch-nextjs';

import { ScrollToResultsOnChange } from '@/app/(routes)/[locale]/(unauth)/recherche/components/navigation/ScrollToResultsOnChange';
import type { EntityType } from '@/lib/config/SearchEngine/entityFields';
import { getEntityFields } from '@/lib/config/SearchEngine/entityFields';
import type { EntityFilterConfig } from '@/lib/config/SearchEngine/entityFiltersConfig';

import { EntitySearchConfigProvider } from './context/EntitySearchConfigContext';
import { EntityControlsBar } from './layout/EntityControlsBar';
import { EntityPagination } from './layout/EntityPagination';
import { EntitySearchHeader } from './layout/EntitySearchHeader';
import { EntitySearchResults } from './layout/EntitySearchResults';
import { createEntitySearchClient } from './lib/entitySearchClient';

// ============================================================================
// Types
// ============================================================================

/**
 * Props du composant EntitySearchInterface
 */
export interface EntitySearchInterfaceProps<T extends BaseHit = BaseHit> {
  /**
   * Clé API Meilisearch
   */
  apiKey: string;

  /**
   * Nom de l'index Meilisearch
   * @example 'auteurs', 'editeurs', 'relieurs'
   */
  indexName: string;

  /**
   * Type d'entité
   */
  entityType: EntityType;

  /**
   * Titre de la page
   * @example "Nos auteurs", "Nos éditeurs"
   */
  title?: string;

  /**
   * Placeholder du champ de recherche
   * @example "Rechercher un auteur par nom, prénom..."
   */
  searchPlaceholder: string;

  /**
   * Configuration déclarative des filtres
   */
  filtersConfig: EntityFilterConfig[];

  /**
   * Fonction render prop pour afficher une carte de résultat
   * Permet la customisation complète du rendu selon l'entité
   *
   * @param hit - Résultat de recherche (type dépend de l'entité)
   */
  renderHitCard: (hit: T) => React.ReactNode;
  /**
   * Messages personnalisés pour l'état NoResults
   */
  customNoResultsMessages?: {
    titre?: string;
    message?: string | React.ReactNode;
  };

  /**
   * Afficher un lien vers la recherche globale si aucun résultat
   * @default false
   */
  linkToGlobalSearchOnNoResults?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Interface de recherche générique pour les entités
 *
 * Architecture SOLID:
 * - Single Responsibility: Orchestration uniquement
 * - Open/Closed: Extensible via render props
 * - Liskov Substitution: Fonction render prop substituable
 * - Dependency Inversion: Dépend des abstractions (config, render props)
 *
 * @example
 * ```tsx
 * <EntitySearchInterface
 *   apiKey={apiKey}
 *   indexName="auteurs"
 *   entityType="auteurs"
 *   title="Nos auteurs"
 *   searchPlaceholder="Rechercher un auteur..."
 *   filtersConfig={AUTEURS_FILTERS_CONFIG}
 *   renderHitCard={(hit, layout) => <AuteurCard auteur={hit} layout={layout} />}
 * />
 * ```
 */
export function EntitySearchInterface<T extends BaseHit = BaseHit>({
  apiKey,
  indexName,
  entityType,
  title,
  renderHitCard,
  searchPlaceholder,
  filtersConfig,
  customNoResultsMessages,
  linkToGlobalSearchOnNoResults = false,
}: EntitySearchInterfaceProps<T>) {
  // ============================================================================
  // State Management
  // ============================================================================

  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

  // ============================================================================
  // Memoized Values
  // ============================================================================

  /**
   * SearchClient singleton - réutilisé pour éviter les reconfigurations
   */
  const searchClient = useMemo(
    () => createEntitySearchClient(apiKey),
    [apiKey],
  );

  /**
   * Champs à récupérer selon le type d'entité
   * Optimise la taille de la réponse
   */
  const attributesToRetrieve = useMemo(
    () => [...getEntityFields(entityType)],
    [entityType],
  );

  // ============================================================================
  // Callbacks
  // ============================================================================

  const handleCloseFilters = useCallback(() => {
    setIsFiltersModalOpen(false);
  }, []);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <EntitySearchConfigProvider
      entityType={entityType}
      indexName={indexName}
      customNoResultsMessages={customNoResultsMessages}
      linkToGlobalSearchOnNoResults={linkToGlobalSearchOnNoResults}
    >
      <InstantSearchNext
        searchClient={searchClient}
        indexName={indexName}
        routing
        future={{
          preserveSharedStateOnUnmount: true,
        }}
      >
        {/* Configuration globale: champs à récupérer */}
        <Configure attributesToRetrieve={attributesToRetrieve} />
        {/* Scroll automatique vers les résultats lors des changements */}
        <ScrollToResultsOnChange />
        {/* Header avec SearchBox */}
        <EntitySearchHeader title={title} placeholder={searchPlaceholder} />
        {/* TODO: Boutons mobile Filtrer/Trier (sticky) */}
        {/* <div className="sticky top-[var(--eo-header-height)] z-10 bg-white lg:hidden">
            <MobileFilterButtons
              onOpenFilters={handleOpenFilters}
              onOpenSort={handleOpenSort}
            />
          </div> */}
        {/* Barre de contrôles */}
        <EntityControlsBar indexName={indexName} entityType={entityType} />
        {/* Layout principal avec grid */}
        <div className="mt-6 grid grid-cols-1 gap-8">
          {/* Sidebar avec filtres */}
          {/* {filtersConfig.length > 0 && (
            <EntitySearchSidebar
              filtersConfig={filtersConfig}
              isFiltersModalOpen={isFiltersModalOpen}
              onCloseFilters={handleCloseFilters}
            />
          )} */}

          {/* Zone principale: Résultats */}
          <div className="space-y-8 lg:col-span-3">
            <EntitySearchResults renderHitCard={renderHitCard} />
            <EntityPagination />
          </div>
        </div>
      </InstantSearchNext>
    </EntitySearchConfigProvider>
  );
}
